import { logger } from '../../../utils/logger.js'
import maplibregl from 'maplibre-gl'
import { isGeoJSONSource } from '../map-engine.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import type { Feature } from '../../../schemas/feature.js'
import {
  buildLineStringFeature,
  buildPolygonFeature,
  buildPreviewLineFeature,
  buildPolygonPreviewFeature,
} from '../../../lib/drawing-geometry.js'

const log = logger.create('map')

export interface LinePolygonDrawingCallbacks {
  onFeatureCreated: (feature: Feature) => void
  onFeatureSelected: (featureId: string) => void
  updateContextMode: () => void
  getShapeComponent: (featureId: string) => { enterEditMode: () => void } | undefined
}

export class LinePolygonDrawingManager {
  private map: maplibregl.Map | undefined

  private drawingPoints: maplibregl.LngLat[] = []
  private isDrawing = false
  private isFinishing = false
  private tempDrawingSourceId = 'temp-drawing'
  private tempDrawingFillLayerId = 'temp-drawing-fill'
  private tempDrawingStrokeLayerId = 'temp-drawing-stroke'
  private drawingHandleMarkers: maplibregl.Marker[] = []
  private mousePreviewSourceId = 'mouse-preview'
  private mousePreviewLayerId = 'mouse-preview-line'

  private tempDrawingLayer:
    | { sourceId: string; layerId?: string; fillLayerId?: string; strokeLayerId?: string }
    | undefined
  private drawingHandles: { markers: maplibregl.Marker[] } | undefined
  private mousePreviewLayer: { sourceId: string; layerId: string } | undefined

  constructor(private callbacks: LinePolygonDrawingCallbacks) {}

  setMap(map: maplibregl.Map): void {
    this.map = map
  }

  get isCurrentlyDrawing(): boolean {
    return this.isDrawing
  }

  get drawingPointCount(): number {
    return this.drawingPoints.length
  }

  addPointToLine(latLng: maplibregl.LngLat): void {
    this.drawingPoints.push(latLng)
    this.isDrawing = true

    // Remove previous temp drawing
    if (this.map) {
      if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
        this.map.removeLayer(this.tempDrawingStrokeLayerId)
      }
      if (this.map.getSource(this.tempDrawingSourceId)) {
        this.map.removeSource(this.tempDrawingSourceId)
      }
    }

    // Create line with solid segments for completed parts
    const lineFeature = buildLineStringFeature(this.drawingPoints)

    if (this.map && lineFeature) {
      this.map.addSource(this.tempDrawingSourceId, {
        type: 'geojson',
        data: lineFeature,
      })

      this.map.addLayer({
        id: this.tempDrawingStrokeLayerId,
        type: 'line',
        source: this.tempDrawingSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      })
    }

    this.tempDrawingLayer = {
      sourceId: this.tempDrawingSourceId,
      layerId: this.tempDrawingStrokeLayerId,
    }

    // Add handles for each point during drawing
    this.addDrawingHandles()
  }

  addPointToPolygon(latLng: maplibregl.LngLat): void {
    this.drawingPoints.push(latLng)
    this.isDrawing = true

    // Remove previous temp drawing
    if (this.map) {
      if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
        this.map.removeLayer(this.tempDrawingStrokeLayerId)
      }
      if (this.map.getLayer(this.tempDrawingFillLayerId)) {
        this.map.removeLayer(this.tempDrawingFillLayerId)
      }
      if (this.map.getSource(this.tempDrawingSourceId)) {
        this.map.removeSource(this.tempDrawingSourceId)
      }
    }

    // Create polygon with solid edges for completed parts
    const polygonFeature = buildPolygonFeature(this.drawingPoints)

    if (this.map && polygonFeature) {
      this.map.addSource(this.tempDrawingSourceId, {
        type: 'geojson',
        data: polygonFeature,
      })

      this.map.addLayer({
        id: this.tempDrawingFillLayerId,
        type: 'fill',
        source: this.tempDrawingSourceId,
        paint: {
          'fill-color': '#3f5a7a',
          'fill-opacity': 0.2,
        },
      })

      this.map.addLayer({
        id: this.tempDrawingStrokeLayerId,
        type: 'line',
        source: this.tempDrawingSourceId,
        paint: {
          'line-color': '#3f5a7a',
          'line-width': 2,
          'line-opacity': 0.8,
        },
      })
    }

    this.tempDrawingLayer = {
      sourceId: this.tempDrawingSourceId,
      fillLayerId: this.tempDrawingFillLayerId,
      strokeLayerId: this.tempDrawingStrokeLayerId,
    }

    // Add handles for each point during drawing
    this.addDrawingHandles()
  }

  private addDrawingHandles(): void {
    // Remove existing drawing handles
    if (this.drawingHandles) {
      this.drawingHandleMarkers.forEach(marker => marker.remove())
      this.drawingHandleMarkers = []
    }

    this.drawingHandles = { markers: [] }

    // Add handle for each drawing point
    this.drawingPoints.forEach((point, index) => {
      const isFirstPoint = index === 0
      const isLastPoint = index === this.drawingPoints.length - 1

      const handleElement = document.createElement('div')
      handleElement.style.width = '10px'
      handleElement.style.height = '10px'
      handleElement.style.cursor = 'grab'
      handleElement.style.zIndex = '2000'
      handleElement.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'

      if (isFirstPoint && this.drawingPoints.length > 2) {
        handleElement.style.background = '#3f5a7a'
        handleElement.style.border = '2px solid #3f5a7a'
        handleElement.style.borderRadius = '50%'
      } else if (isLastPoint && this.drawingPoints.length > 1) {
        handleElement.style.background = '#ef4444'
        handleElement.style.border = '2px solid #ef4444'
        handleElement.style.borderRadius = '50%'
      } else {
        handleElement.style.background = '#ffffff'
        handleElement.style.border = '1px solid #000000'
        handleElement.style.borderRadius = '0'
      }

      const marker = new maplibregl.Marker({
        element: handleElement,
        anchor: 'center',
      }).setLngLat([point.lng, point.lat])

      // Add click handlers for completion
      handleElement.addEventListener('click', (e): void => {
        e.stopPropagation()

        const tool = tripFeatureManager.getSelectedTool()
        if (tool === 'line' && isLastPoint && this.drawingPoints.length >= 2) {
          void this.finishLineDrawing()
        } else if (
          tool === 'polygon' &&
          (isFirstPoint || isLastPoint) &&
          this.drawingPoints.length >= 3
        ) {
          void this.finishPolygonDrawing()
        }
      })

      // Add drag functionality for vertex adjustment during drawing
      this.setupDrawingHandleDragging(marker, index)

      if (this.map) {
        marker.addTo(this.map)
        this.drawingHandleMarkers.push(marker)
      }
    })

    this.drawingHandles.markers = this.drawingHandleMarkers
  }

  private setupDrawingHandleDragging(marker: maplibregl.Marker, vertexIndex: number): void {
    let isDragging = false
    const element = marker.getElement()

    element.addEventListener('mousedown', (e): void => {
      e.stopPropagation()
      e.preventDefault()
      isDragging = true

      if (this.map) {
        const mouseMoveHandler = (e: maplibregl.MapMouseEvent): void => {
          if (isDragging) {
            this.drawingPoints[vertexIndex] = e.lngLat
            marker.setLngLat([e.lngLat.lng, e.lngLat.lat])
            this.updateTempDrawingLayer()
          }
        }

        const mouseUpHandler = (e: maplibregl.MapMouseEvent): void => {
          e.originalEvent.stopPropagation()
          e.originalEvent.preventDefault()
          isDragging = false
          this.map!.off('mousemove', mouseMoveHandler)
          this.map!.off('mouseup', mouseUpHandler)
        }

        this.map.on('mousemove', mouseMoveHandler)
        this.map.on('mouseup', mouseUpHandler)
      }
    })
  }

  private updateTempDrawingLayer(): void {
    const tool = tripFeatureManager.getSelectedTool()

    if (this.tempDrawingLayer && this.map) {
      // Remove the old layers
      if (
        this.tempDrawingLayer.fillLayerId &&
        this.map.getLayer(this.tempDrawingLayer.fillLayerId)
      ) {
        this.map.removeLayer(this.tempDrawingLayer.fillLayerId)
      }
      if (
        this.tempDrawingLayer.strokeLayerId &&
        this.map.getLayer(this.tempDrawingLayer.strokeLayerId)
      ) {
        this.map.removeLayer(this.tempDrawingLayer.strokeLayerId)
      }
      if (this.tempDrawingLayer.layerId && this.map.getLayer(this.tempDrawingLayer.layerId)) {
        this.map.removeLayer(this.tempDrawingLayer.layerId)
      }
      if (this.map.getSource(this.tempDrawingLayer.sourceId)) {
        this.map.removeSource(this.tempDrawingLayer.sourceId)
      }

      if (tool === 'polygon') {
        const polygonFeature = buildPolygonFeature(this.drawingPoints)

        if (polygonFeature) {
          this.map.addSource(this.tempDrawingSourceId, {
            type: 'geojson',
            data: polygonFeature,
          })

          this.map.addLayer({
            id: this.tempDrawingFillLayerId,
            type: 'fill',
            source: this.tempDrawingSourceId,
            paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.2 },
          })

          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 2, 'line-opacity': 0.8 },
          })

          this.tempDrawingLayer = {
            sourceId: this.tempDrawingSourceId,
            fillLayerId: this.tempDrawingFillLayerId,
            strokeLayerId: this.tempDrawingStrokeLayerId,
          }
        }
      } else if (tool === 'line') {
        const lineFeature = buildLineStringFeature(this.drawingPoints)

        if (lineFeature) {
          this.map.addSource(this.tempDrawingSourceId, {
            type: 'geojson',
            data: lineFeature,
          })

          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 3, 'line-opacity': 0.8 },
          })

          this.tempDrawingLayer = {
            sourceId: this.tempDrawingSourceId,
            layerId: this.tempDrawingStrokeLayerId,
          }
        }
      }
    }
  }

  async finishLineDrawing(): Promise<void> {
    if (this.isFinishing || this.drawingPoints.length < 2) {
      return
    }
    this.isFinishing = true

    try {
      const points = this.drawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))
      const feature = await tripFeatureManager.createLineFeature(points)
      this.callbacks.onFeatureCreated(feature)
      this.cleanupTempDrawing()

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }

      log.debug('Line feature created and selected for editing:', feature.id)
    } catch (error) {
      log.error('Failed to create line feature:', error)
    } finally {
      this.isFinishing = false
    }
  }

  async finishPolygonDrawing(): Promise<void> {
    if (this.isFinishing || this.drawingPoints.length < 3) {
      return
    }
    this.isFinishing = true

    try {
      const points = this.drawingPoints.map(p => ({ lat: p.lat, lng: p.lng }))
      const feature = await tripFeatureManager.createPolygonFeature(points)
      this.callbacks.onFeatureCreated(feature)
      this.cleanupTempDrawing()

      tripFeatureManager.selectFeature(feature.id)
      this.callbacks.updateContextMode()

      const shapeComponent = this.callbacks.getShapeComponent(feature.id)
      if (shapeComponent) {
        shapeComponent.enterEditMode()
      }

      log.debug('Polygon feature created and selected for editing:', feature.id)
    } catch (error) {
      log.error('Failed to create polygon feature:', error)
    } finally {
      this.isFinishing = false
    }
  }

  updateMousePreview(mousePos: maplibregl.LngLat): void {
    if (this.drawingPoints.length === 0 || !this.map) {
      return
    }

    const tool = tripFeatureManager.getSelectedTool()

    if (tool === 'polygon' && this.drawingPoints.length >= 2) {
      const polyPreview = buildPolygonPreviewFeature(this.drawingPoints, mousePos)!

      const polySource = this.map.getSource(this.tempDrawingSourceId)
      if (isGeoJSONSource(polySource)) {
        polySource.setData(polyPreview)
      } else {
        this.map.addSource(this.tempDrawingSourceId, {
          type: 'geojson',
          data: polyPreview,
        })
        if (!this.map.getLayer(this.tempDrawingFillLayerId)) {
          this.map.addLayer({
            id: this.tempDrawingFillLayerId,
            type: 'fill',
            source: this.tempDrawingSourceId,
            paint: { 'fill-color': '#3f5a7a', 'fill-opacity': 0.2 },
          })
        }
        if (!this.map.getLayer(this.tempDrawingStrokeLayerId)) {
          this.map.addLayer({
            id: this.tempDrawingStrokeLayerId,
            type: 'line',
            source: this.tempDrawingSourceId,
            paint: { 'line-color': '#3f5a7a', 'line-width': 2, 'line-opacity': 0.8 },
          })
        }
        this.tempDrawingLayer = {
          sourceId: this.tempDrawingSourceId,
          fillLayerId: this.tempDrawingFillLayerId,
          strokeLayerId: this.tempDrawingStrokeLayerId,
        }
      }

      if (this.mousePreviewLayer) {
        if (this.map.getLayer(this.mousePreviewLayerId)) {
          this.map.removeLayer(this.mousePreviewLayerId)
        }
        if (this.map.getSource(this.mousePreviewSourceId)) {
          this.map.removeSource(this.mousePreviewSourceId)
        }
        this.mousePreviewLayer = undefined
      }
      return
    }

    const lastPoint = this.drawingPoints[this.drawingPoints.length - 1]
    const geojson = buildPreviewLineFeature(lastPoint, mousePos)

    if (this.mousePreviewLayer) {
      const source = this.map.getSource(this.mousePreviewSourceId)
      if (isGeoJSONSource(source)) {
        source.setData(geojson)
        return
      }
    }

    this.map.addSource(this.mousePreviewSourceId, {
      type: 'geojson',
      data: geojson,
    })

    this.map.addLayer({
      id: this.mousePreviewLayerId,
      type: 'line',
      source: this.mousePreviewSourceId,
      paint: {
        'line-color': '#666666',
        'line-width': 2,
        'line-opacity': 0.6,
        'line-dasharray': [4, 4],
      },
    })

    this.mousePreviewLayer = {
      sourceId: this.mousePreviewSourceId,
      layerId: this.mousePreviewLayerId,
    }
  }

  cleanupTempDrawing(): void {
    try {
      if (this.map) {
        if (this.map.getLayer(this.tempDrawingFillLayerId)) {
          this.map.removeLayer(this.tempDrawingFillLayerId)
        }
        if (this.map.getLayer(this.tempDrawingStrokeLayerId)) {
          this.map.removeLayer(this.tempDrawingStrokeLayerId)
        }
        if (this.map.getSource(this.tempDrawingSourceId)) {
          this.map.removeSource(this.tempDrawingSourceId)
        }
      }
    } catch {
      // Map style may already be destroyed during teardown
    }
    this.tempDrawingLayer = undefined

    // Clean up drawing handles
    if (this.drawingHandles) {
      this.drawingHandleMarkers.forEach(marker => marker.remove())
      this.drawingHandleMarkers = []
    }
    this.drawingHandles = undefined

    // Clean up mouse preview layer
    try {
      if (this.map) {
        if (this.map.getLayer(this.mousePreviewLayerId)) {
          this.map.removeLayer(this.mousePreviewLayerId)
        }
        if (this.map.getSource(this.mousePreviewSourceId)) {
          this.map.removeSource(this.mousePreviewSourceId)
        }
      }
    } catch {
      // Map style may already be destroyed during teardown
    }
    this.mousePreviewLayer = undefined

    this.drawingPoints = []
    this.isDrawing = false
    this.isFinishing = false
  }

  completeOrCancelCurrentDrawing(previousTool: string): void {
    log.debug(`🔄 Switching from ${previousTool} tool - completing/canceling current drawing`)

    if (this.isDrawing && this.drawingPoints.length > 0) {
      switch (previousTool) {
        case 'line':
          if (this.drawingPoints.length >= 2) {
            log.debug('✅ Auto-completing line with', this.drawingPoints.length, 'points')
            void this.finishLineDrawing()
          } else {
            log.debug('🚫 Canceling incomplete line (need at least 2 points)')
            this.cleanupTempDrawing()
          }
          break

        case 'polygon':
          if (this.drawingPoints.length >= 3) {
            log.debug('✅ Auto-completing polygon with', this.drawingPoints.length, 'points')
            void this.finishPolygonDrawing()
          } else {
            log.debug('🚫 Canceling incomplete polygon (need at least 3 points)')
            this.cleanupTempDrawing()
          }
          break

        case 'point':
          this.cleanupTempDrawing()
          break

        default:
          log.debug('🚫 Canceling unknown drawing type:', previousTool)
          this.cleanupTempDrawing()
          break
      }
    } else {
      this.cleanupTempDrawing()
    }
  }

  dispose(): void {
    this.cleanupTempDrawing()
  }
}
